import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { Button, Card, Input, Label } from "@onemileauto/ui";
import { AuthApiError, login } from "../../lib/auth-client";

/**
 * Custom-branded login page — the real fix for the original problem this
 * whole pass exists to solve: Cloudflare Access's hosted login page is
 * account-wide branding (shared with an unrelated Access app on the same
 * Cloudflare account), so it could never carry One Mile Auto's own look.
 * This is a real route (`/login`, see router.tsx), not an external
 * redirect — matches the vintage-heritage token set the rest of the app
 * uses (packages/ui/src/theme.css), same care given to the earlier design-
 * revision pass, since this is the first screen every user sees.
 *
 * Deliberately no keyboard-shortcut dependence, big touch targets (h-12
 * inputs/buttons), and a plain-language generic error message — Robert and
 * staff are non-technical and likely checking mail from a phone.
 */
export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      // Hard navigation (not router.navigate): the simplest correct way to
      // guarantee a completely fresh TanStack Query cache for the new
      // session rather than reasoning about what might be stale from
      // before login.
      window.location.href = "/";
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 429) {
        setError(err.message || "Too many attempts. Please wait a few minutes and try again.");
      } else if (err instanceof AuthApiError) {
        setError(err.message || "Incorrect username or password.");
      } else {
        setError("Couldn't reach the mail server. Check your connection and try again.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm p-6 sm:p-8">
        <h1 className="font-display text-2xl text-charcoal sm:text-3xl">Sign in</h1>
        <p className="mt-1.5 text-sm text-muted">Welcome back to One Mile Auto Mail.</p>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="login-username">Username</Label>
            <Input
              id="login-username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">Password</Label>
              <Link to="/forgot-password" className="text-sm font-medium text-rust hover:text-rust-deep">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="login-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted hover:text-charcoal"
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2.5 text-sm text-danger">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" disabled={submitting} className="mt-1 w-full">
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
